/**
 * HTTP client for the public team-phone routes (/api/join/<pin>).
 *
 * Separate from utils/api.ts on purpose: a team phone has no account, so it
 * sends no cookies and no Clerk token. It sends only its device token.
 */
import axios from 'axios';
import config from '../config';
import { getDeviceToken, setDeviceToken } from './deviceToken';

const joinApi = axios.create({
  baseURL: config.apiBaseUrl,
  withCredentials: false,
  timeout: 12000,
  headers: { 'Content-Type': 'application/json' },
});

joinApi.interceptors.request.use((req) => {
  const token = getDeviceToken();
  if (token) {
    req.headers['X-Device-Token'] = token;
  }
  return req;
});

// Keep a token the server issues. The first page load and the check-in
// response can carry one.
joinApi.interceptors.response.use((res) => {
  const token = res.data?.data?.deviceToken;
  if (typeof token === 'string') setDeviceToken(token);
  return res;
});

export interface JoinEvent {
  pin: string;
  title: string;
  venueName: string;
  date: string;
  status: 'LIVE' | 'COMPLETE' | 'DRAFT';
  checkInEnabled: boolean;
  answerMode?: AnswerMode;
}

export interface JoinMe {
  teamId: string;
  name: string;
  playerCount: number;
  canonicalTeamId: number | null;
}

export interface JoinReturning {
  name: string;
  playerCount: number;
  canonicalTeamId: number | null;
  canonicalName: string | null;
}

export interface VenueTeam {
  id: number;
  name: string;
  checkedIn: boolean;
}

export async function fetchJoin(pin: string) {
  const res = await joinApi.get(`/api/join/${pin}`);
  return res.data.data as { event: JoinEvent; me: JoinMe | null; returning: JoinReturning | null };
}

export async function fetchVenueTeams(pin: string) {
  const res = await joinApi.get(`/api/join/${pin}/teams`);
  return res.data.data.teams as VenueTeam[];
}

export async function submitCheckin(
  pin: string,
  body: { name: string; playerCount: number; canonicalTeamId: number | null },
) {
  const res = await joinApi.post(`/api/join/${pin}`, body);
  return res.data.data as { me: JoinMe; created: boolean };
}

export type AnswerMode = 'PAPER' | 'PHONE' | 'MIXED';

export interface PlayRound {
  roundNumber: number;
  title: string;
  roundType: 'STANDARD' | 'VISUAL' | 'AUDIO';
  twoAnswerMode: boolean;
  // choiceCount is set for a multiple choice question: the phone shows that
  // many letter buttons. The correct choice never comes to the phone.
  questions: { id: string; number: number; type: 'TEXT' | 'SONG' | 'IMAGE'; choiceCount?: number }[];
}

/** Where the host is. Never carries an answer. */
export interface PlayView {
  kind: 'PREGAME' | 'ROUND' | 'QUESTION' | 'REVIEW' | 'BREAK';
  roundNumber?: number;
  questionNumber?: number | null;
  totalQuestions?: number | null;
  roundType?: string | null;
  /** 'SONG' while a song question is on screen, in any round. */
  questionType?: 'TEXT' | 'SONG' | null;
  questionText?: string | null;
  // The choice texts of a multiple choice question on screen now.
  choices?: string[] | null;
  throughRound?: number | null;
}

export interface PlayData {
  answerMode: AnswerMode;
  lockedRounds?: number[];
  rounds?: PlayRound[];
  answers?: { questionId: string; partA: string; partB: string | null; clientSeq: number }[];
  view?: PlayView | null;
}

export async function fetchPlay(pin: string) {
  const res = await joinApi.get(`/api/join/${pin}/play`);
  return res.data.data as PlayData;
}

export async function sendAnswers(
  pin: string,
  answers: { questionId: string; partA: string; partB: string | null; seq: number }[],
) {
  const res = await joinApi.post(`/api/join/${pin}/answers`, { answers });
  return res.data.data as {
    results: { questionId: string; status: 'saved' | 'locked' | 'invalid'; seq: number }[];
    lockedRounds: number[];
  };
}

/** The server's error message, or a plain fallback for network failures. */
export function joinErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const msg = err.response?.data?.message;
    if (typeof msg === 'string' && msg) return msg;
    if (!err.response) return 'No connection. Check the wifi and try again.';
  }
  return fallback;
}

export default joinApi;
